import { BaseViewStore } from 'core/stores';
import { ChannelStore } from 'modules/channel/stores';
import { action, makeObservable } from 'mobx';
import { carrierTypes, lookupRouteName, sourceOfChange, viewStoreName } from 'common/constants';
import _ from 'lodash';
import { CustomLabelForm } from 'modules/order/forms';
import { translate } from 'core/utils';
import { BaasicDropdownViewStore } from 'common/stores';
import { FormHelperService } from 'common/data';
import { LookupStore } from 'modules/lookup/stores';

class CustomLabelViewStore extends BaseViewStore {
	regex = /\{{2}[^{}]*\}{2}/g;

	form = new CustomLabelForm({
		onSuccess: async () => {
			await this.saveCustomLabel();
		},
	});

	constructor(rootStore, { parentViewStore }) {
		super(rootStore);
		makeObservable(this);
		this.parentViewStore = parentViewStore;
		this.channelId = parentViewStore.selectedItem ? parentViewStore.selectedItem.id : null;
		this.channelStore = new ChannelStore(rootStore);
		this.formHelperService = new FormHelperService();
		this.stateDropdownStore = new BaasicDropdownViewStore({
			lookupRoute: lookupRouteName.state,
			field: this.form.$('returnState'),
			store: new LookupStore(this.rootStore),
			itemTemplate: {
				value: 'countryStateCode',
				label: 'name',
			},
			onChangeCallback: this.onStateDropdownChange,
		});
		this.carrierTypeDropdownStore = new BaasicDropdownViewStore(null, false);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		await this.carrierTypeDropdownStore.initialize({
			lookupRoute: lookupRouteName.carrierType,
			field: this.form.$('carrierType'),
			store: new LookupStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'name',
			},
			onChangeCallback: this.onCarrierTypeDropdownChange,
		});
		if (this.carrierTypeDropdownStore.options.length > 0) {
			this.carrierTypeDropdownStore.setValue(this.carrierTypeDropdownStore.options[0]);
			this.onCarrierTypeDropdownChange();
		}
		let data = null;
		let customFields = null;

		if (this.parentViewStore.store_path === viewStoreName.channelListViewStore) {
			// if channel list page, fetch the channel
			this.channel = await this.channelStore.getChannel(this.channelId, {
				embed: [
					'customFieldInputs',
					'customFieldInputs.customFieldInputVariables',
					'customFieldInputs.customFieldInputVariables.customFieldVariable',
				],
			});
		} else {
			this.channel = this.parentViewStore.channel ? this.parentViewStore.channel : null; // edit page : create page
		}
		if (this.channel) {
			const { customFieldInputs, ...customFieldData } = this.channel;
			data = customFieldData;
		} else {
			if (this.parentViewStore.customLabel) {
				const { customFieldInputs, ...customFieldData } = this.parentViewStore.customLabel;
				customFields = customFieldInputs;
				data = customFieldData;
			}
		}
		if (data) {
			this.formHelperService.updateForm(this.form, data, true);
			if (data.useCustomReturnAddress === true) {
				this.stateDropdownStore.setValue({
					value: data.returnState,
					label: data.returnStateName,
				});
				this.form.$('returnState').set('value', {
					value: data.returnState,
					label: data.returnStateName,
				});
			}
			this.initialValues = data;
		}
		this.setAddressFieldsRules(data != null && data.useCustomReturnAddress === true);
		await this.initializeCustomFields(customFields);
		this.resume();
	};

	@action.bound
	onUseCustomReturnAddressChange = field => {
		this.setAddressFieldsRules(field.value);

		this.form.$('returnName').clear();
		this.form.$('returnPhone').clear();
		this.form.$('returnAddress').clear();
		this.form.$('returnCity').clear();
		this.form.$('returnZip').clear();
		this.form.$('returnState').clear();
		this.stateDropdownStore.setValue(null);

		// Revert back to default value if custom Address is false
		if (field.value === false) {
			this.updateLabelPreview();
		}
	};

	@action.bound
	setAddressFieldsRules = value => {
		const rule = value === true ? 'required' : '';

		this.form.$('returnName').set('rules', rule);
		this.form.$('returnPhone').set('rules', rule);
		this.form.$('returnAddress').set('rules', rule);
		this.form.$('returnCity').set('rules', rule);
		this.form.$('returnZip').set('rules', rule);
		this.form.$('returnState').set('rules', rule);
	};

	@action.bound
	initializeCustomFields = async () => {
		if (this.channel && this.channel.customFieldInputs && this.channel.customFieldInputs.length > 0) {
			_.forEach(_.sortBy(this.channel.customFieldInputs, 'fieldNumber'), item => {
				let fieldToAdd = {
					fields: {
						id: item.id,
						value: item.value,
						previousValue: item.value,
						fieldNumber: item.fieldNumber,
						displayDropdown: false,
						lastIndex: 0,
						customFieldInputVariables: [],
					},
				};

				_.forEach(item.customFieldInputVariables, variable => {
					fieldToAdd.fields.customFieldInputVariables.push({
						id: variable.id,
						customFieldVariableId: variable.customFieldVariableId,
						customFieldInputId: variable.customFieldInputId,
						position: variable.position,
						value: variable.value ? variable.value : variable.customFieldVariable.displayName,
					});
				});
				let field = this.form.$('customFieldInputs').add(fieldToAdd);
				this.handleCustomFieldDropdown(field);
			});
		}

		for (let i = 0; i < 4; i++) {
			if (
				this.form.$('customFieldInputs').value.length === 0 ||
				!this.form.$('customFieldInputs').value.find(input => input.fieldNumber === i + 1)
			) {
				let field = this.form.$('customFieldInputs').add({
					fields: {
						value: '',
						previousValue: '',
						fieldNumber: i + 1,
						displayDropdown: false,
						lastIndex: 0,
						customFieldInputVariables: [],
					},
				});
				this.handleCustomFieldDropdown(field);
			}
		}
	};

	@action.bound
	handleCustomFieldDropdown = field => {
		if (field.$('fieldNumber').value === 3 || field.$('fieldNumber').value === 4) {
			field.labelAddon = translate([`CUSTOMLABEL.LABELADDON${field.$('fieldNumber').value}`]);
		}
		field.$('value').set('label', `${translate(['CUSTOMLABEL.CUSTOMFIELD'])}${field.$('fieldNumber').value}`);
		field.customFieldVariableDropdown = new BaasicDropdownViewStore({
			store: new LookupStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'name',
			},
			lookupRoute: lookupRouteName.customFieldVariable,
			onChangeCallback: value => {
				this.onSelectChange(value, field);
			},
			filterCallback: options => {
				// filterCallback is used to store original variables, which can be filtered later when a variable is added/removed
				field.originalVariableOptions = _.map(options, item => {
					return {
						value: _.get(item, 'id'),
						label: _.get(item, 'name'),
						extra: item,
					};
				});
			},
		});
	};

	@action.bound
	saveCustomLabel = async () => {
		this.suspend();
		try {
			this.parentViewStore.closeCustomLabelModal();
			// Carrier type is only used for preview. It should not be sent to backend.
			const { returnState, carrierType, ...data } = this.form.values();
			this.parentViewStore.customLabel = {
				returnStateName: returnState ? returnState.label : '',
				returnState: returnState ? returnState.value : '',
				...data,
			};
			if (this.parentViewStore.store_path === viewStoreName.channelListViewStore || this.channel) {
				await this.channelStore.updateChannelCustomLabel(
					{
						channelId: this.channelId,
						...this.parentViewStore.customLabel,
					},
					sourceOfChange.customizeLabel
				);
				if (this.parentViewStore.store_path === viewStoreName.channelEditViewStore)
					await this.parentViewStore.getChannel();
			}
			this.rootStore.notificationStore.success(['CUSTOMLABEL.UPDATED']);
		} catch (error) {
			this.rootStore.notificationStore.error(['CUSTOMLABEL.ERROR']);
		}
		this.resume();
	};

	// reset to initial values
	@action.bound
	resetValues = async () => {
		this.form.reset();
		const customFieldInputsLength = this.form.$('customFieldInputs').value.length;
		for (let i = 0; i < customFieldInputsLength; i++) {
			this.form.$(`customFieldInputs[${i}]`).del();
		}
		await this.initialize();
		this.updateLabelPreview();
	};

	@action.bound
	onCustomFieldInputChange = (event, field) => {
		let value = event.target.value;
		if (value !== field.$('previousValue').value) {
			let index = this.findIndexOfDifference(field.$('previousValue').value, value); // Returns the index at which the user typed, so that a variable can be inserted into a string in the correct place.
			field.$('lastIndex').set(index);
			field.$('displayDropdown').value = value[index] === '{' ? true : false;
		}
		var outputString = this.handleDelete(value, field);
		field.$('previousValue').value = field.$('value').value;
		this.updateSvgElementById(`customField${field.value.fieldNumber}`, outputString);
		this.filterVariablesInDropdown(field);
	};

	@action.bound
	handleDelete = (value, field) => {
		let deletedVariables = [];
		let previousVariables =
			field.$('customFieldInputVariables').value.length > 0
				? field.$('customFieldInputVariables').value.map(variable => `{{${variable.value}}}`)
				: null;
		let currentVariables = value.match(this.regex);
		if (previousVariables && !currentVariables) {
			deletedVariables = previousVariables;
		} else if (previousVariables && currentVariables && !this.arraysEqual(previousVariables, currentVariables)) {
			deletedVariables = previousVariables.filter(m => !currentVariables.some(cm => m === cm));
		}
		if (deletedVariables.length > 0) {
			_.forEach(field.$('customFieldInputVariables').value, item => {
				if (deletedVariables.includes(`{{${item.value}}}`)) {
					let variablesArray = field.$('customFieldInputVariables').map(field => field);
					let key = variablesArray.find(variable => variable.value.value === item.value).key;
					field.$(`customFieldInputVariables`).del(key);
				}
			});

			const pattern = new RegExp(deletedVariables.join('|'), 'g');
			const outputString = field.$('previousValue').value.replace(pattern, ''); // "previousValue" is used because it contains variables before deletion, which allows replacing by pattern
			field.$('value').set(outputString);
			return outputString;
		}
		return value;
	};

	@action.bound
	arraysEqual = (arr1, arr2) => {
		if (arr1.length !== arr2.length) return false;
		for (let i = 0; i < arr1.length; i++) {
			if (arr1[i] !== arr2[i]) return false;
		}
		return true;
	};

	@action.bound
	onSelectChange = (selectedOption, field) => {
		const firstPart = field.value.value.substring(0, field.$('lastIndex').value);
		const secondPart = field.value.value.substring(field.$('lastIndex').value + 1);
		const resultString = firstPart + `{{${selectedOption.extra.displayName}}}` + secondPart;
		field.$('value').set(resultString);
		field.$('previousValue').set(resultString);
		field.$('displayDropdown').set(false);

		let matches = resultString.match(this.regex);
		let index = matches.indexOf(`{{${selectedOption.extra.displayName}}}`);
		field.$('customFieldInputVariables').add({
			fields: {
				fieldNumber: field.value.fieldNumber,
				customFieldInputId: field.value.id,
				customFieldVariableId: selectedOption.value,
				position: index,
				value: selectedOption.extra.displayName,
			},
		});
		this.updateLabelPreview();
		this.filterVariablesInDropdown(field);
	};

	@action.bound
	filterVariablesInDropdown = field => {
		field.customFieldVariableDropdown.options = field.originalVariableOptions.filter(
			option =>
				!field.$('customFieldInputVariables').value.some(variable => variable.customFieldVariableId === option.value)
		);
	};

	@action.bound
	findIndexOfDifference = (originalValue, modifiedValue) => {
		for (let i = 0; i < originalValue.length && i < modifiedValue.length; i++) {
			if (originalValue[i] !== modifiedValue[i]) {
				return i;
			}
		}

		return modifiedValue.length - 1;
	};

	@action.bound setInputs = (input, field) => {
		const key = field.key + 'Input';
		this[key] = input;
		this.onInputChangeUpdatePreview(field);
	};

	@action.bound
	onInputChangeUpdatePreview = field => {
		if (field == null) return;

		let idToUpdate = null;
		let valueToSet = null;
		if (field.key === 'attentionName') {
			idToUpdate = 'attentionName';
			valueToSet = field.value;
		}
		if (this.form.$('useCustomReturnAddress').value) {
			switch (field.key) {
				case 'returnName':
					idToUpdate = 'name';
					valueToSet = field.value;
					break;
				case 'returnPhone':
					idToUpdate = 'phone';
					valueToSet = field.value;
					break;
				case 'returnAddress':
					idToUpdate = field.key;
					valueToSet = field.value;
					break;
				case 'returnZip':
				case 'returnCity':
					idToUpdate = 'returnCityStateAndZip';
					valueToSet = `${this.form.$('returnCity').value} ${
						this.form.$('returnState').value ? this.form.$('returnState').value.label : ''
					} ${this.form.$('returnZip').value}`;
					break;
				default:
					break;
			}
		} else {
			if (field.key == 'sellerName') {
				idToUpdate = 'name';
				valueToSet = field.value;
			} else if (field.key == 'sellerPhone') {
				idToUpdate = 'phone';
				valueToSet = field.value;
			}
		}

		this.updateSvgElementById(idToUpdate, valueToSet);
	};

	onCarrierTypeDropdownChange = async () => {
		await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms for SVG to load
		var svgElement = document.getElementById('svgDocumentId');
		if (svgElement) {
			this.updateLabelPreview();
		}
	};

	@action.bound
	onStateDropdownChange = stateDropdownValue => {
		let valueToSet = `${this.form.$('returnCity').value} ${stateDropdownValue.label} ${this.form.$('returnZip').value}`;
		this.updateSvgElementById('returnCityStateAndZip', valueToSet);
	};

	updateLabelPreview = () => {
		let svgFieldKeys = [];
		if (this.form.$('useCustomReturnAddress').value) {
			svgFieldKeys = ['returnName', 'returnPhone', 'attentionName', 'returnAddress', 'returnZip'];
		} else {
			svgFieldKeys = ['sellerName', 'sellerPhone', 'attentionName'];
			this.updateSvgElementById('returnCityStateAndZip', 'ATLANTA GA 30302');
			this.updateSvgElementById('returnAddress', '18TH C MAIN ROAD');
		}

		svgFieldKeys.forEach(fieldKey => {
			this.onInputChangeUpdatePreview(this.form.$(fieldKey));
		});

		this.form.$('customFieldInputs').value.forEach(customField => {
			this.updateSvgElementById(`customField${customField.fieldNumber}`, customField.value);
		});
	};

	updateSvgElementById(idToUpdate, valueToSet) {
		if (idToUpdate && valueToSet != null) {
			var svgElement = document.getElementById('svgDocumentId');
			if (svgElement && svgElement.contentDocument) {
				this.updatePositioning(svgElement);
				var textElementToUpdate = svgElement.contentDocument.getElementById(idToUpdate);
				if (textElementToUpdate) {
					textElementToUpdate.textContent = valueToSet;
				}
			}
		}
	}

	// Used because some elements are positioned differently on UPS and Stamps label when attentionName is provided
	updatePositioning = svgElement => {
		let attentionNameElement = svgElement.contentDocument.getElementById('attentionName');
		let nameElement = svgElement.contentDocument.getElementById('name');
		let phoneElement = svgElement.contentDocument.getElementById('phone');
		let returnAddressElement = svgElement.contentDocument.getElementById('returnAddress');
		let returnCityStateAndZipElement = svgElement.contentDocument.getElementById('returnCityStateAndZip');

		if (
			attentionNameElement &&
			this.carrierTypeDropdownStore.value &&
			this.carrierTypeDropdownStore.value.label === carrierTypes.ups
		) {
			if (this.form.$('attentionName').value) {
				attentionNameElement.setAttribute('y', 12.655);
				nameElement.setAttribute('y', 41.655);
				phoneElement.setAttribute('y', 26.655);
				returnAddressElement.setAttribute('y', 54.655);
				returnCityStateAndZipElement.setAttribute('y', 68.655);
			} else {
				attentionNameElement.setAttribute('y', 0);
				nameElement.setAttribute('y', 21.655);
				phoneElement.setAttribute('y', 35.655);
				returnAddressElement.setAttribute('y', 49.655);
				returnCityStateAndZipElement.setAttribute('y', 63.655);
			}
		} else if (
			attentionNameElement &&
			this.carrierTypeDropdownStore.value &&
			this.carrierTypeDropdownStore.value.label === carrierTypes.stamps
		) {
			if (this.form.$('attentionName').value) {
				attentionNameElement.setAttribute('y', 232.48);
				nameElement.setAttribute('y', 252.48);
				returnAddressElement.setAttribute('y', 273.48);
				returnCityStateAndZipElement.setAttribute('y', 294.48);
			} else {
				attentionNameElement.setAttribute('y', 0);
				nameElement.setAttribute('y', 239.48);
				returnAddressElement.setAttribute('y', 260.48);
				returnCityStateAndZipElement.setAttribute('y', 281.48);
			}
		}
	};
}

export default CustomLabelViewStore;



// WEBPACK FOOTER //
// ./src/modules/channel/stores/CustomLabelViewStore.js